import React, { useState, useEffect } from "react";
import { ThumbsUp, User, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Comments({ articleSlug }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    comment: "",
  });

  // Fetch comments
  useEffect(() => {
    fetchComments();
  }, [articleSlug]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${API}/api/articles/${articleSlug}/comments`
      );
      setComments(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.comment) {
      toast.error("Please fill in your name and comment");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/api/articles/${articleSlug}/comments`, {
        article_slug: articleSlug,
        name: formData.name,
        email: formData.email || undefined,
        comment: formData.comment,
      });

      toast.success("Comment posted successfully!");
      setFormData({ name: "", email: "", comment: "" });
      fetchComments(); // Refresh comments
    } catch (error) {
      console.error("Error posting comment:", error);
      toast.error("Failed to post comment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (commentId) => {
    try {
      const response = await axios.post(`${API}/api/comments/${commentId}/like`);
      // Update local state
      setComments(
        comments.map((c) =>
          c.id === commentId ? { ...c, likes: response.data.likes } : c
        )
      );
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="mt-16 border-t border-[#E2E8F4] pt-12">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <MessageCircle className="w-6 h-6 text-[#2E6BF6]" />
        <h2 className="text-2xl font-bold text-[#0A1F47]">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h2>
      </div>

      {/* Comment Form */}
      <div className="bg-[#F5F7FA] rounded-xl p-6 mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Your name *"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="px-4 py-3 rounded-lg border border-[#E2E8F4] focus:outline-none focus:border-[#2E6BF6] transition-colors"
              required
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="px-4 py-3 rounded-lg border border-[#E2E8F4] focus:outline-none focus:border-[#2E6BF6] transition-colors"
            />
          </div>
          <textarea
            placeholder="Add a comment..."
            value={formData.comment}
            onChange={(e) =>
              setFormData({ ...formData, comment: e.target.value })
            }
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-[#E2E8F4] focus:outline-none focus:border-[#2E6BF6] transition-colors resize-none"
            required
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn-navy disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Posting..." : "Post Comment"}
            </button>
          </div>
        </form>
      </div>

      {/* Comments List */}
      {loading ? (
        <div className="text-center py-8 text-[#0A1F47]/60">
          Loading comments...
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-[#0A1F47]/60">
          No comments yet. Be the first to comment!
        </div>
      ) : (
        <div className="space-y-6">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="bg-white rounded-xl p-6 border border-[#E2E8F4] hover:border-[#2E6BF6]/30 transition-colors"
            >
              <div className="flex gap-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-[#2E6BF6]/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#2E6BF6]" />
                  </div>
                </div>

                {/* Comment Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-[#0A1F47]">
                      {comment.name}
                    </span>
                    <span className="text-sm text-[#0A1F47]/50">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>

                  <p className="text-[#0A1F47]/80 leading-relaxed mb-3">
                    {comment.comment}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLike(comment.id)}
                      className="flex items-center gap-2 text-sm text-[#0A1F47]/60 hover:text-[#2E6BF6] transition-colors"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span>{comment.likes > 0 ? comment.likes : ""}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
